import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import moment from 'moment-timezone';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/api';

export interface EmergencyCodeTarget {
  employeeId?: string | null;
  employeeName?: string | null;
  deviceId?: string | null;
  deviceName?: string | null;
  campaignName?: string | null;
  currentStatus?: string | null;
}

interface GenerateEmergencyCodeModalProps {
  open: boolean;
  onClose: () => void;
  target?: EmergencyCodeTarget | null;
}

const REASON_OPTIONS = [
  { value: 'teks_application_issue', label: 'TEKS TIME application issue' },
  { value: 'internet_api_outage', label: 'Internet/API outage' },
  { value: 'agent_malfunction', label: 'Agent malfunction' },
  { value: 'clock_attendance_issue', label: 'Clock/attendance issue' },
  { value: 'supervisor_operational_exception', label: 'Supervisor-authorized operational exception' },
  { value: 'it_troubleshooting', label: 'IT troubleshooting' },
  { value: 'other', label: 'Other' },
];

const DURATION_OPTIONS = [15, 30, 60];

const GenerateEmergencyCodeModal: React.FC<GenerateEmergencyCodeModalProps> = ({ open, onClose, target }) => {
  const [reasonCode, setReasonCode] = useState('teks_application_issue');
  const [reasonText, setReasonText] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<{
    code: string;
    expiresAt: string;
    bypassDurationMinutes: number;
  } | null>(null);
  const [countdown, setCountdown] = useState('');

  const { data: requiredActionData } = useQuery({
    queryKey: ['requiredAction', target?.employeeId, target?.deviceId],
    queryFn: () => apiClient.monitoring.getRequiredAction({
      employee_id: target?.employeeId || undefined,
      device_id: target?.deviceId || undefined,
    }),
    enabled: open && !!(target?.employeeId || target?.deviceId),
  });

  const currentStatus = useMemo(() => {
    if (target?.currentStatus) return target.currentStatus;
    const detail = requiredActionData?.data?.data;
    if (!detail) return 'Unknown';
    return detail.authorization_state?.replace(/_/g, ' ') || detail.required_action?.replace(/_/g, ' ') || 'Unknown';
  }, [target?.currentStatus, requiredActionData]);

  useEffect(() => {
    if (!generated) return undefined;
    const timer = setInterval(() => {
      const remaining = moment(generated.expiresAt).diff(moment(), 'seconds');
      if (remaining <= 0) {
        setCountdown('Expired');
        clearInterval(timer);
        return;
      }
      const mins = Math.floor(remaining / 60);
      const secs = remaining % 60;
      setCountdown(`${mins}:${String(secs).padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(timer);
  }, [generated]);

  const resetForm = () => {
    setReasonCode('teks_application_issue');
    setReasonText('');
    setDurationMinutes(30);
    setError(null);
    setGenerated(null);
    setCountdown('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleGenerate = async () => {
    setError(null);
    if (reasonCode === 'other' && !reasonText.trim()) {
      setError('Explanation is required when reason is Other.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiClient.monitoring.generateEmergencyCode({
        employee_id: target?.employeeId || undefined,
        device_id: target?.deviceId || undefined,
        reason_code: reasonCode,
        reason_text: reasonCode === 'other' ? reasonText.trim() : undefined,
        duration_minutes: durationMinutes,
      });
      const payload = response.data?.data;
      setGenerated({
        code: payload.code,
        expiresAt: payload.expires_at,
        bypassDurationMinutes: payload.bypass_duration_minutes ?? durationMinutes,
      });
    } catch (err: any) {
      const message = err?.response?.status === 403
        ? 'You do not have permission to generate emergency codes.'
        : (err?.response?.data?.message || 'Failed to generate emergency code.');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!generated?.code) return;
    await navigator.clipboard.writeText(generated.code);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{generated ? 'Emergency Access Code' : 'Generate Emergency Access Code'}</DialogTitle>
      <DialogContent>
        {!generated ? (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Employee" value={target?.employeeName || '—'} InputProps={{ readOnly: true }} fullWidth />
            <TextField label="Device" value={target?.deviceName || '—'} InputProps={{ readOnly: true }} fullWidth />
            <TextField label="Current Status" value={currentStatus} InputProps={{ readOnly: true }} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select value={reasonCode} label="Reason" onChange={(e) => setReasonCode(String(e.target.value))}>
                {REASON_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {reasonCode === 'other' && (
              <TextField
                label="Explanation"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                required
              />
            )}
            <FormControl fullWidth>
              <InputLabel>Bypass Duration</InputLabel>
              <Select
                value={durationMinutes}
                label="Bypass Duration"
                onChange={(e) => setDurationMinutes(Number(e.target.value))}
              >
                {DURATION_OPTIONS.map((minutes) => (
                  <MenuItem key={minutes} value={minutes}>{minutes} minutes</MenuItem>
                ))}
              </Select>
            </FormControl>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="h3" textAlign="center" letterSpacing={4}>{generated.code}</Typography>
            <Typography variant="body2">Valid for: {countdown || '—'}</Typography>
            <Typography variant="body2">Bypass Duration: {generated.bypassDurationMinutes} minutes</Typography>
            <Typography variant="body2">Device: {target?.deviceName || '—'}</Typography>
            <Typography variant="body2">Employee: {target?.employeeName || '—'}</Typography>
            <Alert severity="info">This code will not be shown again after you close this dialog.</Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!generated ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" onClick={handleGenerate} disabled={submitting}>
              {submitting ? 'Generating…' : 'Generate Emergency Code'}
            </Button>
          </>
        ) : (
          <>
            <Button startIcon={<ContentCopy />} onClick={handleCopy}>Copy Code</Button>
            <Button variant="contained" onClick={handleClose}>Close</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateEmergencyCodeModal;
